import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/context/ThemeContext';
import { 
  QrCode, Smartphone, MessageCircle, Zap, Check, ArrowRight,
  Store, ShoppingBag, Utensils, Building2, Sun, Moon,
  FileText, Globe, Star, GripVertical, BarChart3, Palette,
  ChevronRight, Sparkles, Share2, Layout, Download, 
  Users, Image, Menu, X
} from 'lucide-react';

const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, isVisible];
};

const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
        transitionDelay: `${delay}ms`
      }}
    >
      {children}
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'glass shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
              <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
                <QrCode className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="text-xl font-extrabold font-['Manrope'] tracking-tight">IziCatalog</span>
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors font-medium">Fonctionnalit&eacute;s</a>
              <a href="#showcase" className="text-sm text-muted-foreground hover:text-foreground transition-colors font-medium">Mode Site Web</a>
              <a href="#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors font-medium">Tarifs</a>
            </nav>
            
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle" className="rounded-full">
                {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              </Button>
              <Button variant="ghost" onClick={() => navigate('/login')} className="hidden sm:inline-flex text-sm font-medium" data-testid="header-login">
                Connexion
              </Button>
              <Button onClick={() => navigate('/register')} className="rounded-full text-sm font-semibold px-5" data-testid="header-cta">
                Essai gratuit
              </Button>
              <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden glass border-t p-4 space-y-2">
            <a href="#features" onClick={() => setMobileMenuOpen(false)} className="block py-2 px-3 rounded-lg hover:bg-muted text-sm font-medium">{"Fonctionnalit\u00e9s"}</a>
            <a href="#showcase" onClick={() => setMobileMenuOpen(false)} className="block py-2 px-3 rounded-lg hover:bg-muted text-sm font-medium">Mode Site Web</a>
            <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="block py-2 px-3 rounded-lg hover:bg-muted text-sm font-medium">Tarifs</a>
            <button onClick={() => { navigate('/login'); setMobileMenuOpen(false); }} className="block w-full text-left py-2 px-3 rounded-lg hover:bg-muted text-sm font-medium">Connexion</button>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative pt-28 pb-8 sm:pt-36 sm:pb-16 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-[10%] w-72 h-72 bg-primary/8 rounded-full blur-3xl" />
          <div className="absolute top-40 right-[5%] w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="space-y-8">
              <AnimatedSection>
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border bg-card text-sm font-medium" data-testid="hero-badge">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span>{"Nouveau : Mode Site Web avec drag & drop"}</span>
                </div>
              </AnimatedSection>
              
              <AnimatedSection delay={100}>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight font-['Manrope'] leading-[1.1]">
                  {"Votre vitrine digitale."}<br />
                  <span className="bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">
                    {"Pr\u00eate en 10 minutes."}
                  </span>
                </h1>
              </AnimatedSection>

              <AnimatedSection delay={200}>
                <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-lg">
                  {"Cr\u00e9ez votre catalogue digital, g\u00e9n\u00e9rez un QR code, et transformez-le en mini-site web. Vos clients d\u00e9couvrent vos produits en un scan."}
                </p>
              </AnimatedSection>

              <AnimatedSection delay={300}>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button 
                    size="lg" 
                    onClick={() => navigate('/register')}
                    className="rounded-full text-base px-8 h-13 font-semibold group"
                    data-testid="hero-cta-primary"
                  >
                    {"Cr\u00e9er mon catalogue"}
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    onClick={() => navigate('/s/boutique-demo')}
                    className="rounded-full text-base px-8 h-13 font-medium"
                    data-testid="hero-cta-secondary"
                  >
                    <Smartphone className="mr-2 w-4 h-4" />
                    {"Voir la d\u00e9mo"}
                  </Button>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={400}>
                <div className="flex items-center gap-6 pt-2">
                  <div className="flex items-center gap-1.5">
                    <Check className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm text-muted-foreground">Gratuit pour essayer</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Check className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm text-muted-foreground">Sans engagement</span>
                  </div>
                </div>
              </AnimatedSection>
            </div>
            
            <AnimatedSection delay={200} className="relative">
              <div className="relative">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/49058d0f-900f-40d8-ae93-410a1a8378e1/images/8661d57bbabfbc0ca886031a8e433ec6abf89ead473d750f57b9438947f05b78.png"
                  alt="Catalogue digital sur mobile"
                  className="w-full h-auto rounded-2xl"
                  data-testid="hero-image"
                />
                <div className="absolute -bottom-4 -left-4 sm:-bottom-6 sm:-left-6 bg-card rounded-xl shadow-xl p-3 sm:p-4 border animate-slide-in">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                      <QrCode className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-500" />
                    </div>
                    <div>
                      <p className="font-bold text-sm">{"QR Code pr\u00eat !"}</p>
                      <p className="text-xs text-muted-foreground">{"Scannez pour d\u00e9couvrir"}</p>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 bg-card rounded-xl shadow-xl p-3 border animate-fade-in" style={{ animationDelay: '600ms' }}>
                  <div className="flex items-center gap-2">
                    <div className="flex -space-x-1">
                      <div className="w-6 h-6 rounded-full bg-blue-500" />
                      <div className="w-6 h-6 rounded-full bg-emerald-500" />
                      <div className="w-6 h-6 rounded-full bg-amber-500" />
                    </div>
                    <span className="text-xs font-semibold">{"+250 commer\u00e7ants"}</span>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {[
              { value: '10 min', label: "Pour cr\u00e9er votre catalogue" },
              { value: 'QR Code', label: "G\u00e9n\u00e9ration instantan\u00e9e" },
              { value: '100%', label: 'Mobile-first' },
              { value: '24/7', label: 'Catalogue accessible' }
            ].map((stat, i) => (
              <AnimatedSection key={i} delay={i * 100}>
                <div className="text-center p-4">
                  <p className="text-2xl sm:text-3xl font-extrabold font-['Manrope'] text-primary">{stat.value}</p>
                  <p className="text-sm text-muted-foreground mt-1">{stat.label}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Manrope'] mb-4">
                {"Fini les photos WhatsApp d\u00e9sorganis\u00e9es"}
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
                {"Vos clients m\u00e9ritent une exp\u00e9rience professionnelle pour d\u00e9couvrir vos produits"}
              </p>
            </div>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: <MessageCircle className="w-5 h-5" />, title: "Catalogue d\u00e9sorganis\u00e9", desc: "Les clients ne retrouvent plus vos produits dans les conversations", color: 'text-red-500 bg-red-500/10' },
              { icon: <Users className="w-5 h-5" />, title: 'Clients perdus', desc: "Trop d'informations \u00e9parpill\u00e9es entre WhatsApp, Instagram et Facebook", color: 'text-amber-500 bg-amber-500/10' },
              { icon: <Zap className="w-5 h-5" />, title: "Messages r\u00e9p\u00e9titifs", desc: "Vous r\u00e9pondez aux m\u00eames questions sur les prix et la disponibilit\u00e9", color: 'text-orange-500 bg-orange-500/10' },
            ].map((problem, i) => (
              <AnimatedSection key={i} delay={i * 120}>
                <div className="p-6 rounded-2xl border bg-card hover:shadow-lg transition-all duration-300">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${problem.color}`}>
                    {problem.icon}
                  </div>
                  <h3 className="font-bold mb-2">{problem.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{problem.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-muted/40">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-14">
              <span className="inline-block text-primary font-semibold text-sm mb-3 tracking-wide uppercase">{"Fonctionnalit\u00e9s"}</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Manrope'] mb-4">
                Tout pour digitaliser votre commerce
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
                {"De la cr\u00e9ation de votre catalogue \u00e0 la r\u00e9ception de clients, chaque outil est con\u00e7u pour simplifier votre quotidien"}
              </p>
            </div>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: <Store className="w-5 h-5" />, title: 'Catalogue complet', desc: "Cat\u00e9gories, produits, variantes, photos multiples et prix promotionnels avec compte \u00e0 rebours.", tag: null },
              { icon: <QrCode className="w-5 h-5" />, title: 'QR Code vitrine', desc: "G\u00e9n\u00e9rez un QR code unique \u00e0 afficher en vitrine. Vos clients acc\u00e8dent \u00e0 votre catalogue en un scan.", tag: null },
              { icon: <Layout className="w-5 h-5" />, title: 'Mode Site Web', desc: "Transformez votre catalogue en mini-site avec un constructeur drag & drop : banni\u00e8res, FAQ, vid\u00e9os...", tag: 'Premium' },
              { icon: <Download className="w-5 h-5" />, title: 'Export PDF', desc: "T\u00e9l\u00e9chargez votre catalogue en PDF professionnel A4, personnalisable avec votre slogan et vos cat\u00e9gories.", tag: null },
              { icon: <Share2 className="w-5 h-5" />, title: "R\u00e9seaux sociaux", desc: "Liens Instagram, Facebook, TikTok et partage WhatsApp int\u00e9gr\u00e9s directement sur vos fiches produits.", tag: null },
              { icon: <Star className="w-5 h-5" />, title: 'Avis Google', desc: "Affichez une bulle anim\u00e9e invitant vos clients \u00e0 laisser un avis Google directement depuis votre page.", tag: 'Nouveau' },
              { icon: <Sparkles className="w-5 h-5" />, title: 'IA pour descriptions', desc: "G\u00e9n\u00e9rez et am\u00e9liorez vos descriptions de produits avec l'intelligence artificielle en un clic.", tag: 'Premium' },
              { icon: <BarChart3 className="w-5 h-5" />, title: 'Statistiques', desc: "Suivez les vues, les produits populaires et les tendances avec un tableau de bord analytique complet.", tag: null },
              { icon: <Palette className="w-5 h-5" />, title: 'Personnalisation', desc: "Th\u00e8mes, couleurs, banni\u00e8re hero personnalisable, logo... Votre catalogue \u00e0 votre image.", tag: null },
            ].map((feature, i) => (
              <AnimatedSection key={i} delay={i * 80}>
                <div className="group relative p-6 bg-card rounded-2xl border hover:shadow-lg hover:border-primary/30 transition-all duration-300 h-full">
                  {feature.tag && (
                    <span className={`absolute top-4 right-4 text-[11px] font-bold px-2.5 py-0.5 rounded-full ${
                      feature.tag === 'Premium' ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400' : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                    }`}>
                      {feature.tag}
                    </span>
                  )}
                  <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary/15 transition-colors">
                    <div className="text-primary">{feature.icon}</div>
                  </div>
                  <h3 className="font-bold mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{feature.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Website Builder Showcase */}
      <section id="showcase" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <AnimatedSection>
              <div className="space-y-6">
                <span className="inline-flex items-center gap-2 text-primary font-semibold text-sm tracking-wide uppercase">
                  <Layout className="w-4 h-4" /> Mode Site Web
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Manrope'] leading-tight">
                  {"Construisez votre site web en glissant-d\u00e9posant"}
                </h2>
                <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                  {"Allez au-del\u00e0 du simple catalogue. Avec le mode Site Web, ajoutez des sections personnalis\u00e9es pour cr\u00e9er un v\u00e9ritable mini-site professionnel."}
                </p>
                <div className="space-y-4 pt-2">
                  {[
                    { icon: <Image className="w-4 h-4" />, text: "Banni\u00e8re Hero personnalisable (couleur, bouton, lien)" },
                    { icon: <GripVertical className="w-4 h-4" />, text: "Drag & drop pour r\u00e9ordonner les sections" },
                    { icon: <FileText className="w-4 h-4" />, text: "6 types de sections : Banni\u00e8re, Texte, Produits, Cat\u00e9gories, Vid\u00e9o, FAQ" },
                    { icon: <Globe className="w-4 h-4" />, text: "Page publique mobile-first optimis\u00e9e" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                        <div className="text-primary">{item.icon}</div>
                      </div>
                      <span className="text-sm font-medium leading-relaxed">{item.text}</span>
                    </div>
                  ))}
                </div>
                <Button 
                  onClick={() => navigate('/register')}
                  className="rounded-full px-6 mt-4 font-semibold group"
                  data-testid="showcase-cta"
                >
                  Essayer le Mode Site Web
                  <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={200}>
              <div className="relative">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/49058d0f-900f-40d8-ae93-410a1a8378e1/images/80b4f8c61bb2faae645391baaa936a851e5cdd30d0536459cbeecdf487446141.png"
                  alt="Constructeur de site web drag and drop"
                  className="w-full h-auto rounded-2xl shadow-2xl"
                  data-testid="showcase-image"
                />
                <div className="absolute -bottom-4 -right-4 bg-card rounded-xl shadow-xl p-3 border">
                  <div className="flex items-center gap-2">
                    <GripVertical className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs font-bold">Drag & Drop</span>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-muted/40">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-14">
              <span className="inline-block text-primary font-semibold text-sm mb-3 tracking-wide uppercase">Processus</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Manrope']">
                {"Pr\u00eat en 4 \u00e9tapes simples"}
              </h2>
            </div>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { num: '1', icon: <Store className="w-5 h-5" />, title: "Cr\u00e9ez votre boutique", desc: "Renseignez vos informations en quelques clics" },
              { num: '2', icon: <ShoppingBag className="w-5 h-5" />, title: 'Ajoutez vos produits', desc: "Photos, prix, descriptions et cat\u00e9gories. Tout est personnalisable" },
              { num: '3', icon: <QrCode className="w-5 h-5" />, title: "G\u00e9n\u00e9rez votre QR code", desc: "T\u00e9l\u00e9chargez-le et affichez-le en vitrine ou sur vos supports" },
              { num: '4', icon: <MessageCircle className="w-5 h-5" />, title: 'Recevez des contacts', desc: "Vos clients consultent et vous contactent via WhatsApp ou t\u00e9l\u00e9phone" },
            ].map((step, i) => (
              <AnimatedSection key={i} delay={i * 120}>
                <div className="relative text-center p-6">
                  <div className="w-14 h-14 bg-primary text-primary-foreground rounded-2xl flex items-center justify-center mx-auto mb-5 text-xl font-bold shadow-lg shadow-primary/25">
                    {step.icon}
                  </div>
                  <span className="absolute top-3 right-3 text-xs font-bold text-primary/40">0{step.num}</span>
                  <h3 className="font-bold mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Target audience */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Manrope'] mb-4">
                {"Con\u00e7u pour tous les commer\u00e7ants"}
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground">
                {"Que vous vendiez des v\u00eatements, de la nourriture ou des services"}
              </p>
            </div>
          </AnimatedSection>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: <Store className="w-7 h-7" />, name: "Commerces de proximit\u00e9" },
              { icon: <ShoppingBag className="w-7 h-7" />, name: 'Boutiques & Mode' },
              { icon: <Utensils className="w-7 h-7" />, name: 'Restaurants & Snacks' },
              { icon: <Building2 className="w-7 h-7" />, name: 'Showrooms & Salons' },
            ].map((audience, i) => (
              <AnimatedSection key={i} delay={i * 100}>
                <div className="flex flex-col items-center p-6 bg-card rounded-2xl border hover:shadow-md hover:border-primary/30 transition-all duration-300 group cursor-default">
                  <div className="text-primary mb-3 group-hover:scale-110 transition-transform">{audience.icon}</div>
                  <span className="font-semibold text-center text-sm">{audience.name}</span>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-muted/40">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-14">
              <span className="inline-block text-primary font-semibold text-sm mb-3 tracking-wide uppercase">Tarifs</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Manrope'] mb-4">
                Des tarifs simples et transparents
              </h2>
              <p className="text-muted-foreground">{"Sans engagement, r\u00e9siliable \u00e0 tout moment"}</p>
            </div>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-3 gap-6">
            <AnimatedSection delay={0}>
              <PricingCard 
                name="Starter" 
                price={19} 
                desc={"Pour d\u00e9marrer votre pr\u00e9sence digitale"}
                features={[
                  "1 boutique",
                  "3 cat\u00e9gories max",
                  "30 produits max",
                  "QR code du catalogue",
                  "Page publique mobile",
                  "Contact WhatsApp"
                ]}
                onSelect={() => navigate('/register')}
              />
            </AnimatedSection>
            <AnimatedSection delay={120}>
              <PricingCard 
                name="Pro" 
                price={39} 
                recommended
                desc={"Le plus populaire pour les commer\u00e7ants actifs"}
                features={[
                  "1 boutique",
                  "Cat\u00e9gories illimit\u00e9es",
                  "Produits illimit\u00e9s",
                  "Th\u00e8mes et couleurs personnalisables",
                  "Banni\u00e8res promotionnelles",
                  "Produits mis en avant",
                  "Statistiques d\u00e9taill\u00e9es",
                  "R\u00e9seaux sociaux int\u00e9gr\u00e9s",
                  "Export PDF du catalogue"
                ]}
                onSelect={() => navigate('/register')}
              />
            </AnimatedSection>
            <AnimatedSection delay={240}>
              <PricingCard 
                name="Premium" 
                price={59} 
                desc={"Le pack complet pour une pr\u00e9sence en ligne pro"}
                features={[
                  "Tout le plan Pro",
                  "Mode Site Web (drag & drop)",
                  "Banni\u00e8re Hero personnalisable",
                  "6 types de sections",
                  "Bulle d'avis Google",
                  "IA pour descriptions",
                  "2 boutiques",
                  "Support prioritaire"
                ]}
                onSelect={() => navigate('/register')}
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="max-w-4xl mx-auto">
            <div className="relative rounded-3xl bg-primary p-8 sm:p-14 text-center overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(255,255,255,0.1)_0%,_transparent_60%)]" />
              <div className="relative">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Manrope'] mb-4 text-primary-foreground">
                  {"Lancez votre catalogue digital d\u00e8s aujourd'hui"}
                </h2>
                <p className="text-base sm:text-lg text-primary-foreground/80 mb-8 max-w-xl mx-auto">
                  {"Rejoignez les commer\u00e7ants qui ont digitalis\u00e9 leur vitrine. Configuration en 10 minutes, sans comp\u00e9tences techniques."}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button 
                    size="lg" 
                    variant="secondary"
                    onClick={() => navigate('/register')}
                    className="rounded-full text-base px-8 font-semibold group"
                    data-testid="final-cta"
                  >
                    Commencer gratuitement
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    onClick={() => navigate('/s/boutique-demo')}
                    className="rounded-full text-base px-8 font-medium border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
                    data-testid="final-demo-cta"
                  >
                    Voir un exemple
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* Footer */}
      <footer className="py-10 px-4 sm:px-6 lg:px-8 border-t">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <QrCode className="w-4 h-4 text-primary-foreground" />
              </div>
              <span className="font-extrabold font-['Manrope']">IziCatalog</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/legal/mentions-legales" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Mentions legales</Link>
              <Link to="/legal/politique-confidentialite" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Confidentialite</Link>
              <Link to="/legal/cgu" className="text-sm text-muted-foreground hover:text-foreground transition-colors">CGU</Link>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Tarifs</Link>
              <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Connexion</Link>
              <Link to="/register" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Inscription</Link>
            </div>
          </div>
          <div className="mt-6 text-center">
            <p className="text-muted-foreground text-sm">
              {"© 2025 IziCatalog — SARL DEAL RUN. Tous droits r\u00e9serv\u00e9s."}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const PricingCard = ({ name, price, desc, features, recommended, onSelect }) => (
  <div className={`relative p-7 bg-card rounded-2xl border-2 transition-all duration-300 h-full flex flex-col ${
    recommended 
      ? 'border-primary shadow-xl shadow-primary/10 scale-[1.02]' 
      : 'border-border hover:border-primary/40 hover:shadow-lg'
  }`} data-testid={`pricing-card-${name.toLowerCase()}`}>
    {recommended && (
      <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
        <span className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-xs font-bold tracking-wide">
          {"Recommand\u00e9"}
        </span>
      </div>
    )}
    
    <div className="mb-6">
      <h3 className="text-lg font-bold mb-1">{name}</h3>
      <p className="text-xs text-muted-foreground">{desc}</p>
    </div>
    <div className="mb-6">
      <span className="text-4xl font-extrabold font-['Manrope']">{price}&euro;</span>
      <span className="text-muted-foreground text-sm">/mois</span>
    </div>
    
    <ul className="space-y-3 mb-8 flex-1">
      {features.map((feature, idx) => (
        <li key={idx} className="flex items-start gap-2.5">
          <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
          <span className="text-sm">{feature}</span>
        </li>
      ))}
    </ul>
    
    <Button 
      className="w-full rounded-full font-semibold"
      variant={recommended ? 'default' : 'outline'}
      onClick={onSelect}
      data-testid={`pricing-cta-${name.toLowerCase()}`}
    >
      Commencer
    </Button>
  </div>
);

export default LandingPage;
